import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";
import type {
    CellStyle,
    ColDef,
    ColGroupDef,
    CustomHeaderGroupProps,
    ICellRendererParams,
    IHeaderParams,
    ITooltipParams,
} from "@/ui/molecules/Grid";
import GridHeaderComponent from "@/ui/molecules/GridHeaderComponent";
import GridChildrenHeaderComponent from "@/ui/molecules/GridChildrenHeaderComponent";
import GridHeaderWithAction from "@/ui/molecules/GridHeaderWithAction";

import GridRendererNumber from "@/ui/organisms/GridRendererNumber";

import { formatStringDateTime } from "@/utils/dates";

import type {
    TAllRowsAvailable,
    TRowAnnualizationWithOperatingStatements,
    TRowReviewWithOperatingStatements,
} from "../typesGrids";
import { formatNumber } from "../utils";

import type { GridAnnualizationProps } from ".";
import GridAnnualizationRendererFirstColumn from "./GridAnnualizationRendererFirstColumn";
import { ReconciliationHeader } from "./ReconciliationHeader";
import { createCalculateColSpanForMergingDuplicatedColumnsCellsWithSameValue } from "../calculateColSpanForMergingDuplicatedColumnsCellsWithSameValue";

const SPACE_IN_PIXELS_PER_NUMERIC_CHARACTER = 8;
const ADDITIONAL_SPACE_IN_PIXELS = 32;

const calculateColumnWidths = (value: number) => {
    const columnWidth =
        value * SPACE_IN_PIXELS_PER_NUMERIC_CHARACTER +
        ADDITIONAL_SPACE_IN_PIXELS;
    const columnMinWidth = value * SPACE_IN_PIXELS_PER_NUMERIC_CHARACTER;
    return { columnWidth, columnMinWidth };
};

const SourceCountBadge = ({ count }: { count: number }) => (
    <Box
        sx={{
            display: "flex",
            padding: "3px 4px",
            justifyContent: "center",
            alignItems: "center",
            gap: "4px",
            borderRadius: "2px",
            border: "1px solid var(--proda-blue-30)",
            background: "var(--proda-white)",
            boxSizing: "border-box",
        }}
    >
        <Typography variant="body4" sx={{ fontWeight: 700, lineHeight: "8px" }}>
            {count}
        </Typography>
    </Box>
);

type ColumnData =
    GridAnnualizationProps["columnDefsOperatingStatements"][string][string][number];

type ChildColumnData = NonNullable<ColumnData["children"]>[number];

export const makeGridAnnualizationColumnDefs = ({
    reconciliationPeriods,
    projectId,
    columnDefsOperatingStatementsFromProps,
    longestOperatingStatementAndTotalsNumericValue,
    extractionsInfo,
    getExtractionReviewUrl,
    onViewExtraction,
    selectedAnnualizationGroup,
    showSource,
}: {
    reconciliationPeriods?: GridAnnualizationProps["reconciliationPeriods"];
    projectId: number;
    columnDefsOperatingStatementsFromProps: GridAnnualizationProps["columnDefsOperatingStatements"];
    longestOperatingStatementAndTotalsNumericValue: number;
    extractionsInfo:
        | {
              fileName: string;
              extractionId: number;
              sheetName: string;
              uploadedAt: string | undefined;
              uploadedBy: string | undefined;
              uploadedByUserId?: number;
              completedAt: string | null | undefined;
          }[]
        | null;
    getExtractionReviewUrl: (params: {
        projectId: number;
        extractionId: number;
    }) => string;
    onViewExtraction: ({
        projectId,
        extractionId,
    }: {
        projectId: number;
        extractionId: number;
    }) => void;
    selectedAnnualizationGroup: "month" | "quarter" | "half" | "year";
    showSource: boolean;
}) => {
    const cellStylesWithEllipsis = {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        display: "block",
    };

    const commonColumnParameters = {
        suppressMovable: true,
        cellStyle: cellStylesWithEllipsis,
    };

    const columnDefAccount = {
        headerName: "Account Mapping",
        headerComponent: GridHeaderComponent,
        headerComponentParams: {
            sx: { padding: "4px 8px" },
        },
        field: "account",
        width: 348,
        minWidth: 348,
        resizable: false,
        cellRenderer: GridAnnualizationRendererFirstColumn,
        cellStyle: {
            ...commonColumnParameters.cellStyle,
            paddingRight: "0px",
            paddingLeft: "0px",
        },
        pinned: "left" as const,
        suppressMovable: true,
    };

    const columnWidths = calculateColumnWidths(
        longestOperatingStatementAndTotalsNumericValue,
    );

    const numberCellRenderer = (params: ICellRendererParams) => (
        <GridRendererNumber errorGroups={{}} {...params} />
    );

    const paddedCellStyle = (): CellStyle => ({
        ...commonColumnParameters.cellStyle,
        paddingRight: "0px",
        paddingLeft: "0px",
    });

    const createTooltipValueGetter =
        (field: string | undefined) =>
        (params: ITooltipParams<TRowReviewWithOperatingStatements>) => {
            if (!params.data || field === undefined) return null;
            const value = params.data[field];
            if (typeof value !== "number") return null;
            return formatNumber(value);
        };

    const createExtractionRedirect = (extractionId: number | undefined) =>
        extractionId !== undefined
            ? () =>
                  onViewExtraction({
                      projectId,
                      extractionId: Number(extractionId),
                  })
            : undefined;

    const createExtractionUrl = (extractionId: number | undefined) =>
        extractionId !== undefined
            ? getExtractionReviewUrl({ projectId, extractionId })
            : undefined;

    const extractionInfoById:
        | Record<number, (typeof extractionsInfo & object)[number]>
        | undefined = extractionsInfo
        ? Object.fromEntries(
              extractionsInfo.map(info => [info.extractionId, info]),
          )
        : undefined;

    const findExtractionInfo = (extractionId: number | undefined) => {
        if (extractionId === undefined || !extractionInfoById) {
            return undefined;
        }
        return extractionInfoById[Number(extractionId)];
    };

    const formatExtractionHeaderName = (
        info: ReturnType<typeof findExtractionInfo>,
        extractionIdOverride?: number | string,
    ) => {
        const id = extractionIdOverride ?? info?.extractionId;
        return `${info?.sheetName} Table ${id} > ${info?.fileName}`;
    };

    const formatExtractionTooltip = (
        info: ReturnType<typeof findExtractionInfo>,
    ) => {
        const uploadedAtText =
            info?.uploadedAt !== undefined
                ? formatStringDateTime(info.uploadedAt)
                : "N/A";
        return `Data from file: "${info?.fileName}", uploaded at: ${uploadedAtText}, sheet: "${info?.sheetName}", extraction ID: ${info?.extractionId}.`;
    };

    const yearGroupHeaderComponent = (props: CustomHeaderGroupProps) => (
        <GridHeaderComponent
            {...props}
            textAlign="left"
            sx={{
                padding: "4px 8px",
                border: "0.5px solid var(--proda-white-150)",
            }}
        />
    );

    const totalHeaderComponent = (
        params: IHeaderParams | CustomHeaderGroupProps,
    ) => (
        <GridHeaderComponent
            {...params}
            textAlign="right"
            sx={{ padding: "4px 8px", fontWeight: 700 }}
        />
    );

    const periodGroupHeaderComponent = (
        params: IHeaderParams | CustomHeaderGroupProps,
    ) => (
        <GridHeaderComponent
            {...params}
            textAlign="left"
            sx={{
                padding: "4px 8px",
                backgroundColor: "var(--proda-neutral-20)",
                border: "0.5px solid var(--proda-white-150)",
            }}
        />
    );

    const yearlyHeaderComponent = (
        params: IHeaderParams | CustomHeaderGroupProps,
    ) => (
        <GridHeaderComponent
            {...params}
            textAlign="left"
            sx={{
                padding: "4px 8px",
                border: "0.5px solid var(--proda-white-150)",
            }}
        />
    );

    const monthlyTotalHeaderComponent = (params: CustomHeaderGroupProps) => (
        <GridHeaderComponent
            {...params}
            textAlign="right"
            sx={{ padding: "4px 8px", fontWeight: 700 }}
            contentStyle={{
                justifyContent: "flex-end",
                alignItems: "flex-end",
                display: "flex",
                height: "100%",
            }}
        />
    );

    const quarterGroupHeaderComponent = (params: CustomHeaderGroupProps) => (
        <GridHeaderComponent
            {...params}
            textAlign="left"
            sx={{
                padding: "4px 8px",
                backgroundColor: "var(--proda-neutral-20)",
                border: "0.5px solid var(--proda-white-150)",
            }}
        />
    );

    const createYearGroupDef = <T,>(year: string, children: T[]) => ({
        headerName: year,
        groupId: year,
        marryChildren: true,
        suppressHeaderContextMenu: true,
        headerGroupComponent: yearGroupHeaderComponent,
        children,
    });

    const buildSourceChildren = (
        column: ColumnData,
        headerName: string,
        tooltipContent: string,
    ) => {
        const primaryChild = {
            colSpan:
                createCalculateColSpanForMergingDuplicatedColumnsCellsWithSameValue(
                    { relyOnGridApiExpandedValue: false },
                )<TRowAnnualizationWithOperatingStatements>,
            field: column.field,
            headerName,
            width: columnWidths.columnWidth,
            minWidth: columnWidths.columnMinWidth,
            headerComponent: (params: IHeaderParams) => (
                <GridChildrenHeaderComponent
                    {...params}
                    selected={true}
                    groupHeaderName={column.headerName}
                    tooltipContent={tooltipContent}
                    redirect={createExtractionRedirect(column.extractionId)}
                    url={createExtractionUrl(column.extractionId)}
                    sx={{
                        border: "0.5px solid var(--proda-white-150)",
                    }}
                />
            ),
            suppressHeaderContextMenu: true,
            cellRenderer: numberCellRenderer,
        };

        const secondaryChildren = (column.children ?? []).map(
            (child: ChildColumnData) => {
                const childInfo = findExtractionInfo(child.extractionId);
                const childHeaderName = formatExtractionHeaderName(
                    childInfo,
                    child.extractionId,
                );
                const childTooltipContent = formatExtractionTooltip(childInfo);

                return {
                    headerName: childHeaderName,
                    field: child.field,
                    width: columnWidths.columnWidth,
                    minWidth: columnWidths.columnMinWidth,
                    headerComponent: (params: IHeaderParams) => (
                        <GridChildrenHeaderComponent
                            {...params}
                            selected={false}
                            groupHeaderName={column.headerName}
                            tooltipContent={childTooltipContent}
                            redirect={createExtractionRedirect(
                                child.extractionId,
                            )}
                            url={createExtractionUrl(child.extractionId)}
                            sx={{
                                border: "0.5px solid var(--proda-white-150)",
                            }}
                        />
                    ),
                    suppressHeaderContextMenu: true,
                    cellRenderer: numberCellRenderer,
                    tooltipValueGetter: createTooltipValueGetter(child.field),
                    cellStyle: paddedCellStyle,
                };
            },
        );

        return [primaryChild, ...secondaryChildren];
    };

    const buildMonthlyShowSourceColumnDef = (
        column: ColumnData,
        isQuarterTotal: boolean,
    ) => {
        const headerInfo = findExtractionInfo(column.extractionId);
        const headerName = formatExtractionHeaderName(
            headerInfo,
            headerInfo?.extractionId,
        );
        const tooltipContent = formatExtractionTooltip(headerInfo);

        const headerComponent = (
            params: IHeaderParams | CustomHeaderGroupProps,
        ) => (
            <GridHeaderComponent
                {...params}
                textAlign={isQuarterTotal ? "right" : "left"}
                redirect={createExtractionRedirect(column.extractionId)}
                url={createExtractionUrl(column.extractionId)}
                sx={{
                    backgroundColor: isQuarterTotal
                        ? "var(--proda-neutral-20)"
                        : "var(--proda-neon-blue-10)",
                    color: isQuarterTotal ? undefined : "var(--proda-black)",
                    padding: "4px 8px",
                    border: "0.5px solid var(--proda-white-150)",
                    fontWeight: isQuarterTotal ? 700 : undefined,
                }}
                contentStyle={{
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    display: "flex",
                    height: "100%",
                }}
            />
        );

        const headerGroupComponent = (params: CustomHeaderGroupProps) => (
            <GridHeaderWithAction
                {...params}
                textAlign="left"
                sx={{
                    backgroundColor: "var(--proda-neon-blue-10)",
                    color: "var(--proda-black)",
                    padding: "4px 8px",
                    border: "0.5px solid var(--proda-white-150)",
                }}
                action={
                    column.children ? (
                        <SourceCountBadge count={column.children.length + 1} />
                    ) : null
                }
            />
        );

        const children =
            headerInfo === undefined
                ? undefined
                : buildSourceChildren(column, headerName, tooltipContent);

        return {
            suppressHeaderContextMenu: true,
            editable: false,
            resizable: false,
            headerName: isQuarterTotal ? "Total" : column.headerName,
            field: column.field,
            width: columnWidths.columnWidth,
            minWidth: columnWidths.columnMinWidth,
            groupId: column.headerName,
            marryChildren: true,
            tooltipValueGetter: createTooltipValueGetter(column.field),
            cellStyle: paddedCellStyle,
            cellRenderer: numberCellRenderer,
            headerComponent,
            headerGroupComponent,
            children,
        };
    };

    const buildMonthlyNoSourceColumnDef = (
        column: ColumnData,
        isQuarterTotal: boolean,
    ) => {
        const headerComponent = (
            params: IHeaderParams | CustomHeaderGroupProps,
        ) => (
            <GridHeaderWithAction
                {...params}
                textAlign={isQuarterTotal ? "right" : "left"}
                action={
                    column.children ? (
                        <SourceCountBadge
                            count={(column.children?.length ?? 0) + 1}
                        />
                    ) : null
                }
                redirect={createExtractionRedirect(column.extractionId)}
                url={createExtractionUrl(column.extractionId)}
                sx={{
                    backgroundColor: isQuarterTotal
                        ? "var(--proda-neutral-20)"
                        : "var(--proda-neon-blue-10)",
                    color: isQuarterTotal ? undefined : "var(--proda-black)",
                    fontWeight: isQuarterTotal ? 700 : undefined,
                    padding: "4px 8px",
                    border: "0.5px solid var(--proda-white-150)",
                }}
            />
        );

        return {
            editable: false,
            width: columnWidths.columnWidth,
            minWidth: columnWidths.columnMinWidth,
            headerName: isQuarterTotal ? "Total" : column.headerName,
            field: column.field,
            headerComponent,
            cellRenderer: numberCellRenderer,
        };
    };

    const buildMonthlyQuarterDef = (quarter: string, columns: ColumnData[]) => {
        const isTotal =
            columns.some(column => column.month === undefined) &&
            columns.length === 1;

        if (isTotal) {
            return {
                headerName: "Total",
                field: columns[0]?.field,
                marryChildren: true,
                headerComponent: monthlyTotalHeaderComponent,
                suppressHeaderContextMenu: true,
                cellStyle: paddedCellStyle,
                cellRenderer: numberCellRenderer,
            };
        }

        const children = columns.map(column => {
            const isQuarterTotal = column.month === undefined;
            return showSource
                ? buildMonthlyShowSourceColumnDef(column, isQuarterTotal)
                : buildMonthlyNoSourceColumnDef(column, isQuarterTotal);
        });

        return {
            headerName: quarter,
            groupId: quarter,
            marryChildren: true,
            headerGroupComponent: quarterGroupHeaderComponent,
            suppressHeaderContextMenu: true,
            children,
        };
    };

    const buildMonthlyColumnDefs = () => {
        const yearEntries = Object.entries(
            columnDefsOperatingStatementsFromProps,
        );
        return yearEntries.map(([year, quarters]) => {
            const quarterEntries = Object.entries(quarters);
            const quarterDefs = quarterEntries.map(([quarter, columns]) =>
                buildMonthlyQuarterDef(quarter, columns),
            );
            return createYearGroupDef(year, quarterDefs);
        });
    };

    const buildSimplePeriodDef = (
        periodKey: "quarter" | "half",
        periodName: string,
        columns: ColumnData[],
    ) => {
        const isTotal = columns.some(column => column[periodKey] === undefined);

        if (isTotal) {
            return {
                headerName: "Total",
                field: columns[0]?.field,
                width: columnWidths.columnWidth,
                minWidth: columnWidths.columnMinWidth,
                suppressHeaderContextMenu: true,
                headerComponent: totalHeaderComponent,
                cellStyle: paddedCellStyle,
                cellRenderer: numberCellRenderer,
            };
        }

        return {
            headerName: periodName,
            headerComponent: periodGroupHeaderComponent,
            suppressHeaderContextMenu: true,
            field: columns[0]?.field,
            tooltipValueGetter: createTooltipValueGetter(columns[0]?.field),
            cellStyle: paddedCellStyle,
            cellRenderer: numberCellRenderer,
        };
    };

    const buildSimplePeriodColumnDefs = (periodKey: "quarter" | "half") => {
        const yearEntries = Object.entries(
            columnDefsOperatingStatementsFromProps,
        );
        return yearEntries.map(([year, periods]) => {
            const periodEntries = Object.entries(periods);
            const periodDefs = periodEntries.map(([periodName, columns]) =>
                buildSimplePeriodDef(periodKey, periodName, columns),
            );
            return createYearGroupDef(year, periodDefs);
        });
    };

    const buildYearlyColumnDefs = () => {
        const yearEntries = Object.entries(
            columnDefsOperatingStatementsFromProps,
        );
        const unsorted = yearEntries.flatMap(([year, quarters]) => {
            const quarterEntries = Object.entries(quarters);
            return quarterEntries.map(([, columns]) => ({
                headerName: year,
                field: columns[0]?.field,
                resizable: false,
                editable: false,
                width: columnWidths.columnWidth,
                minWidth: columnWidths.columnMinWidth,
                cellStyle: paddedCellStyle,
                headerComponent: yearlyHeaderComponent,
                cellRenderer: numberCellRenderer,
            }));
        });
        return unsorted.toSorted((a, b) =>
            a.headerName.localeCompare(b.headerName),
        );
    };

    const columnDefsOperatingStatementsMonthly =
        selectedAnnualizationGroup === "month" ? buildMonthlyColumnDefs() : [];

    const columnDefsOperatingStatementsQuarterly =
        selectedAnnualizationGroup === "quarter"
            ? buildSimplePeriodColumnDefs("quarter")
            : [];

    const columnDefsOperatingStatementsHalfYearly =
        selectedAnnualizationGroup === "half"
            ? buildSimplePeriodColumnDefs("half")
            : [];

    const columnDefsOperatingStatementsYearly =
        selectedAnnualizationGroup === "year" ? buildYearlyColumnDefs() : [];

    const periodDefs = [
        ...columnDefsOperatingStatementsMonthly,
        ...columnDefsOperatingStatementsQuarterly,
        ...columnDefsOperatingStatementsHalfYearly,
        ...columnDefsOperatingStatementsYearly,
    ];
    // Keep metrics on the period row, preserving the existing column widths.
    const addMetrics = (
        definition: ColDef | ColGroupDef,
        parents: string[] = [],
    ): (ColDef | ColGroupDef)[] => {
        if ("children" in definition && Array.isArray(definition.children)) {
            return [
                {
                    ...definition,
                    children: definition.children.flatMap(child =>
                        addMetrics(child, [
                            ...parents,
                            definition.headerName ?? "",
                        ]),
                    ),
                },
            ];
        }
        const field = "field" in definition ? definition.field : undefined;
        const period = reconciliationPeriods?.find(
            period => period.columns[0]?.field === field,
        );
        if (!period) return [definition];
        const isTotal = definition.headerName === "Total";
        const periodName = isTotal
            ? parents.at(-1) ?? "Total"
            : definition.headerName ?? "";
        const tone = isTotal
            ? parents.length <= 1
                ? "total"
                : "subtotal"
            : "period";
        const partial = period.columns.some(column =>
            column.headerName.includes("to date"),
        );
        const width =
            ("width" in definition ? definition.width : undefined) ?? 110;
        return period.columns.map((column, index) => ({
            ...definition,
            field: column.field,
            colId: column.field,
            headerName: `${periodName} ${column.headerName}`,
            width: partial ? width + 28 + (index === 0 ? 24 : 0) : width,
            minWidth:
                "minWidth" in definition ? definition.minWidth : undefined,
            headerComponent: ReconciliationHeader,
            headerComponentParams: {
                period: periodName,
                part: column.headerName,
                first: index === 0,
                last: index === period.columns.length - 1,
                tone,
            },
            cellStyle: paddedCellStyle,
            cellRenderer: (
                params: ICellRendererParams<
                    TAllRowsAvailable | TRowReviewWithOperatingStatements,
                    number | null
                >,
            ) => (
                <GridRendererNumber
                    errorGroups={{}}
                    {...params}
                    value={params.value ?? null}
                    textColor={
                        column.headerName.startsWith("Budget") ||
                        column.headerName === "Full budget"
                            ? "var(--proda-black-10)"
                            : undefined
                    }
                    varianceFavorableAbove={
                        column.difference === true
                            ? (
                                  params.data as
                                      | TRowReviewWithOperatingStatements
                                      | undefined
                              )?.["reconciliationFavorableAbove"] === true
                            : undefined
                    }
                />
            ),
            tooltipValueGetter: createTooltipValueGetter(column.field),
        }));
    };
    return [
        columnDefAccount,
        ...(reconciliationPeriods
            ? periodDefs.flatMap(definition => addMetrics(definition))
            : periodDefs),
    ];
};
